import { useState, useEffect } from 'react'
import { useLazyQuery, ApolloError } from '@apollo/client'
import {
  GET_USER_RESOURCE_ACTIVITY,
  GET_USER_ACTOR_ACTIVITY,
  GET_CONTACT_RESOURCE_ACTIVITY,
  GET_CONTACT_ACTOR_ACTIVITY
} from '../user-history'
import { HistoryItem, ViewMode } from '../types'
import { filterMeaningfulHistory } from '../utils/historyHelpers'

interface UseHistoryQueryProps {
  userId: string
  sfContactId?: string
  email?: string
  isInternal: boolean
  viewMode: ViewMode
  onDataLoaded: (data: HistoryItem[]) => void
}

interface UseHistoryQueryResult {
  loading: boolean
  error: ApolloError | undefined
  data: HistoryItem[]
  refetch: () => void
}

export const useHistoryQuery = ({
  userId,
  sfContactId,
  email,
  isInternal,
  viewMode,
  onDataLoaded
}: UseHistoryQueryProps): UseHistoryQueryResult => {
  const [historyData, setHistoryData] = useState<HistoryItem[]>([])

  const [getUserResourceActivity, { loading: userResourceLoading, error: userResourceError }] = useLazyQuery(
    GET_USER_RESOURCE_ACTIVITY,
    {
      variables: {
        resourceClass: 'User',
        resourceAttribute: 'id',
        resourceValue: userId
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const filteredData = filterMeaningfulHistory(data.activity || [])
        setHistoryData(filteredData)
        onDataLoaded(filteredData)
      }
    }
  )

  const [getUserActorActivity, { loading: userActorLoading, error: userActorError }] = useLazyQuery(
    GET_USER_ACTOR_ACTIVITY,
    {
      variables: {
        actorClass: 'User',
        actorAttribute: 'id',
        actorValue: userId
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const filteredData = filterMeaningfulHistory(data.activity || [])
        setHistoryData(filteredData)
        onDataLoaded(filteredData)
      }
    }
  )

  const [getContactResourceActivity, { loading: contactResourceLoading, error: contactResourceError }] = useLazyQuery(
    GET_CONTACT_RESOURCE_ACTIVITY,
    {
      variables: {
        resourceClass: 'SfContact',
        resourceAttribute: 'sf_id',
        resourceValue: sfContactId || ''
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const filteredData = filterMeaningfulHistory(data.activity || [])
        setHistoryData(filteredData)
        onDataLoaded(filteredData)
      }
    }
  )

  const [getContactActorActivity, { loading: contactActorLoading, error: contactActorError }] = useLazyQuery(
    GET_CONTACT_ACTOR_ACTIVITY,
    {
      variables: {
        actorClass: 'User',
        actorAttribute: 'email',
        actorValue: email || ''
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const filteredData = filterMeaningfulHistory(data.activity || [])
        setHistoryData(filteredData)
        onDataLoaded(filteredData)
      }
    }
  )

  const executeQuery = () => {
    if (isInternal) {
      if (viewMode === 'by_resource') {
        getUserResourceActivity()
      } else {
        getUserActorActivity()
      }
    } else {
      if (viewMode === 'by_resource') {
        getContactResourceActivity()
      } else if (email) {
        getContactActorActivity()
      }
    }
  }

  useEffect(() => {
    executeQuery()
  }, [isInternal, viewMode, sfContactId, userId, email])

  const loading = userResourceLoading || userActorLoading || contactResourceLoading || contactActorLoading
  const error = userResourceError || userActorError || contactResourceError || contactActorError

  return {
    loading,
    error,
    data: historyData,
    refetch: executeQuery
  }
}
